import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/tanstackstart-react";
import { reportLovableError } from "@/lib/lovable-error-reporting";

type SoftErrorBoundaryProps = {
  children: ReactNode;
  /** Stable id for logs / Lovable (e.g. "NotificationBell"). */
  name: string;
  /** Local fallback — keep it small; do not reuse the full-page root error UI. */
  fallback: ReactNode;
};

type SoftErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Catches render errors in a subtree so they do not bubble to the root
 * TanStack `errorComponent` ("This page didn't load").
 *
 * Root boundary stays as last resort; soft boundaries are additional isolation.
 *
 * Sentry note: errors caught by custom ErrorBoundaries are NOT auto-captured.
 * We must call Sentry.captureException in componentDidCatch (below) — otherwise
 * SoftErrorBoundary would swallow the exact failures we want monitored.
 */
export class SoftErrorBoundary extends Component<SoftErrorBoundaryProps, SoftErrorBoundaryState> {
  state: SoftErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): SoftErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[SoftErrorBoundary:${this.props.name}]`, error, info.componentStack);
    Sentry.captureException(error, {
      tags: { softBoundary: this.props.name },
      extra: { componentStack: info.componentStack },
    });
    reportLovableError(error, {
      boundary: "soft_error_boundary",
      softBoundary: this.props.name,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}
